import { Plus, Repeat, Plane } from 'lucide-react';
import { DAY_NAMES, toISODate, weekDays, typeMeta, fmtTime, shiftHours } from './scheduleUtils';
import { PTO_TYPE_LABELS } from '@/components/hr/hrUtils';

const ShiftChip = ({ shift, onClick, editable }) => {
  const meta = typeMeta(shift.type);
  return (
    <button
      type="button"
      disabled={!editable}
      onClick={(e) => { e.stopPropagation(); onClick?.(shift); }}
      data-testid={`schedule-shift-${shift.shift_id}-${shift.date}`}
      className={`w-full text-left rounded-lg border px-2 py-1 text-[11px] leading-tight ${meta.chip} ${editable ? 'hover:brightness-95 hover:-translate-y-px transition-[transform,filter]' : 'cursor-default'}`}
    >
      <div className="flex items-center gap-1 font-semibold">
        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
        <span className="truncate">{fmtTime(shift.start_time)}–{fmtTime(shift.end_time)}</span>
        {shift.recurring && <Repeat className="w-3 h-3 ml-auto opacity-60" />}
      </div>
      <div className="truncate opacity-80">{shift.label || meta.label}</div>
    </button>
  );
};

const OffChip = ({ off }) => {
  const pending = off.status === 'pending';
  return (
    <div
      data-testid={`schedule-off-${off.employee_id}-${off.date}`}
      data-status={off.status}
      className={`w-full rounded-lg border px-2 py-1 text-[11px] leading-tight ${pending ? 'border-dashed border-amber-300 bg-amber-50 text-amber-800' : 'border-violet-200 bg-violet-50 text-violet-700'}`}
      style={pending ? undefined : { backgroundImage: 'repeating-linear-gradient(135deg, rgba(139,92,246,.10) 0 4px, transparent 4px 8px)' }}
    >
      <div className="flex items-center gap-1 font-semibold"><Plane className="w-3 h-3" /> {pending ? 'Time off requested' : 'Time Off'}</div>
      <div className="truncate opacity-80">{PTO_TYPE_LABELS[off.type] || off.type}</div>
    </div>
  );
};

export const ScheduleGrid = ({ monday, employees, shifts, timeOff, editable, onAdd, onEditShift, showEmployeeColumn = true }) => {
  const days = weekDays(monday);
  const today = toISODate(new Date());
  const byCell = (empId, iso) => shifts.filter((s) => s.employee_id === empId && s.date === iso);
  const offCell = (empId, iso) => timeOff.find((o) => o.employee_id === empId && o.date === iso && o.status === 'approved')
    || timeOff.find((o) => o.employee_id === empId && o.date === iso);

  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="schedule-grid">
      <table className="w-full min-w-[860px] border-collapse text-sm">
        <thead>
          <tr className="bg-slate-50">
            {showEmployeeColumn && <th className="sticky left-0 z-10 bg-slate-50 text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-48">Employee</th>}
            {days.map((d, i) => {
              const iso = toISODate(d);
              return (
                <th key={iso} className={`px-2 py-3 text-center border-l border-slate-100 ${iso === today ? 'bg-cyan-50' : ''}`}>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{DAY_NAMES[i]}</div>
                  <div className={`text-lg font-bold ${iso === today ? 'text-cyan-600' : 'text-slate-800'}`}>{d.getDate()}</div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {employees.length === 0 && (
            <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No employees found.</td></tr>
          )}
          {employees.map((emp) => {
            const weekHours = shifts.filter((s) => s.employee_id === emp.id).reduce((a, s) => a + shiftHours(s), 0);
            return (
              <tr key={emp.id} className="border-t border-slate-100 align-top" data-testid={`schedule-row-${emp.id}`}>
                {showEmployeeColumn && (
                  <td className="sticky left-0 z-10 bg-white px-4 py-3">
                    <div className="font-semibold text-slate-800 truncate">{emp.name}</div>
                    <div className="text-xs text-slate-500 capitalize">{emp.role} · <span data-testid={`schedule-hours-${emp.id}`}>{weekHours % 1 ? weekHours.toFixed(1) : weekHours}h</span></div>
                  </td>
                )}
                {days.map((d) => {
                  const iso = toISODate(d);
                  const cell = byCell(emp.id, iso);
                  const off = offCell(emp.id, iso);
                  const blocked = off?.status === 'approved';
                  return (
                    <td
                      key={iso}
                      onClick={() => editable && !blocked && onAdd?.(emp, iso)}
                      data-testid={`schedule-cell-${emp.id}-${iso}`}
                      className={`group border-l border-slate-100 px-1.5 py-2 min-h-[72px] h-[72px] ${iso === today ? 'bg-cyan-50/40' : ''} ${editable && !blocked ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                    >
                      <div className="flex flex-col gap-1">
                        {off && <OffChip off={off} />}
                        {cell.map((s) => <ShiftChip key={`${s.shift_id}-${s.date}`} shift={s} editable={editable} onClick={onEditShift} />)}
                        {editable && !blocked && (
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex justify-center text-slate-400">
                            <Plus className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
